import type {CSSProperties} from 'react'

import tileGradient1 from '../assets/tile-gradient-1.webp'
import tileGradient2 from '../assets/tile-gradient-2.webp'

const BACKGROUNDS = ['#FF80D2', '#0FBF3E', '#C898FD'] as const
const HOVER_BACKGROUNDS = [...BACKGROUNDS, `url(${tileGradient1})`, `url(${tileGradient2})`]
const REPOSITORY_COLORS = [
  {
    background: 'var(--base-color-scale-purple-5)',
    lightName: 'var(--base-color-scale-purple-5)',
    darkName: 'var(--base-color-scale-purple-2)',
  },
  {
    background: 'var(--base-color-scale-green-4)',
    lightName: 'var(--base-color-scale-green-6)',
    darkName: 'var(--base-color-scale-green-3)',
  },
  {
    background: 'var(--base-color-scale-pink-3)',
    lightName: 'var(--base-color-scale-pink-6)',
    darkName: 'var(--base-color-scale-pink-2)',
  },
] as const

const COLOR_TILES_FIRST = [
  BACKGROUNDS[0],
  tileGradient1,
  BACKGROUNDS[1],
  BACKGROUNDS[2],
  tileGradient2,
  tileGradient1,
  BACKGROUNDS[0],
  BACKGROUNDS[1],
]

function createGridLayout(columns: number, rows: number) {
  let repositoryIndex = 0

  const cells = Array.from({length: columns * rows}, (_, index) => {
    const colIndex = index % columns
    const rowIndex = Math.floor(index / columns)
    let grayBackground = 'transparent'

    if ((colIndex + rowIndex) % 4 === 2) {
      grayBackground = 'var(--brand-color-border-muted)'
    } else if ((colIndex + rowIndex) % 4 === 1) {
      grayBackground = 'var(--brand-color-canvas-subtle)'
    }

    return {
      id: `cell-${colIndex}-${rowIndex}`,
      colIndex,
      rowIndex,
      isLastColumn: colIndex === columns - 1,
      isLastRow: rowIndex === rows - 1,
      repositoryIndex: grayBackground === 'transparent' ? repositoryIndex++ : null,
      grayStyle: {
        '--color': grayBackground,
      } satisfies CSSProperties & {'--color': string},
    }
  })

  const revealCells = cells
    .filter(cell => cell.repositoryIndex !== null)
    .sort(
      (a, b) =>
        a.colIndex + (rows - 1 - a.rowIndex) - (b.colIndex + (rows - 1 - b.rowIndex)) || b.rowIndex - a.rowIndex,
    )

  const colorCells =
    columns === 6 && rows === 4
      ? COLOR_TILES_FIRST.map((_, index) => {
          const colIndex = Math.ceil(index / 2)
          return {colIndex, rowIndex: rows - 1 - (index - colIndex)}
        })
      : columns === 4 && rows === 6
        ? [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
            {colIndex: 3, rowIndex: 1},
            {colIndex: 3, rowIndex: 0},
          ]
        : [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
          ]

  const colorTiles = colorCells.slice(0, COLOR_TILES_FIRST.length).map(({colIndex, rowIndex}, index) => ({
    color: COLOR_TILES_FIRST[index],
    col: colIndex,
    row: rowIndex,
  }))

  return {
    cells: cells
      .map(cell => {
        const colorTile = colorTiles.find(tile => tile.col === cell.colIndex && tile.row === cell.rowIndex)
        const color = colorTile?.color ?? 'transparent'
        const colorBackground = color === tileGradient1 || color === tileGradient2 ? `url(${color})` : color
        const repositoryColors = REPOSITORY_COLORS[(cell.rowIndex * 2 + cell.colIndex) % REPOSITORY_COLORS.length]

        if (!repositoryColors) throw new RangeError('Invalid repository color index')

        return {
          ...cell,
          repositoryStyle: {
            '--repository-background': repositoryColors.background,
            '--repository-name-light': repositoryColors.lightName,
            '--repository-name-dark': repositoryColors.darkName,
            '--repository-backing-top': cell.rowIndex === 0 ? 0 : -1,
            '--repository-backing-right': cell.isLastColumn ? 0 : -1,
            '--repository-backing-bottom': cell.isLastRow ? 0 : -1,
            '--repository-backing-left': cell.colIndex === 0 ? 0 : -1,
          } satisfies CSSProperties & Record<`--repository-${string}`, string | number>,
          colorStyle: {
            '--color': colorBackground,
          } satisfies CSSProperties & {'--color': string},
          hoverStyle: {
            '--color': HOVER_BACKGROUNDS[(cell.rowIndex * 2 + cell.colIndex) % HOVER_BACKGROUNDS.length],
          } satisfies CSSProperties & {'--color': string | undefined},
        }
      })
      // Stable repository DOM order preserves focus when only the grid coordinates change.
      .sort((a, b) => (a.repositoryIndex ?? cells.length) - (b.repositoryIndex ?? cells.length)),
    colorTiles,
    revealCells,
  }
}

export const GRID_LAYOUTS = {
  desktop: createGridLayout(6, 4),
  tablet: createGridLayout(4, 6),
  mobile: createGridLayout(3, 6),
}

export type GridLayout = ReturnType<typeof createGridLayout>
type GridCell = GridLayout['cells'][number]

export function getNeighborState(cell: GridCell, activeRepositoryCells: GridCell[]) {
  return {
    isActive: activeRepositoryCells.some(
      active => Math.max(Math.abs(active.colIndex - cell.colIndex), Math.abs(active.rowIndex - cell.rowIndex)) === 1,
    ),
    isDiagonal: !activeRepositoryCells.some(
      active => Math.abs(active.colIndex - cell.colIndex) + Math.abs(active.rowIndex - cell.rowIndex) === 1,
    ),
  }
}
